/**
 * Wave 70 · Public Housy chat widget for showcase visitors.
 *
 * Floating bottom-right launcher that opens a small chat panel.
 * Talks to POST /v1/public/showcase/:slug/agent — anonymous,
 * rate-limited per IP, baked-in context only.
 *
 * Permissions are enforced server-side. This component never
 * sends auth headers and never assumes any.
 */

import { useEffect, useRef, useState } from 'react';
import { API_ORIGIN } from '../lib/api';
import { t, useLocale } from '../lib/i18n';

type Msg = { role: 'user' | 'assistant'; content: string };

interface Props {
  slug: string;
}

export function PublicHousy({ slug }: Props) {
  // Re-render whenever locale flips so labels track.
  useLocale();
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState<Msg[]>([
    {
      role: 'assistant',
      content:
        'Sou o Housy, concierge da casa. Pergunta sobre área, materiais, status, bairro — respondo rapidinho.',
    },
  ]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to the latest message when history grows or panel opens.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [history, open]);

  const send = async () => {
    const text = draft.trim();
    if (!text || loading) return;
    setError(null);
    setDraft('');
    const next: Msg[] = [...history, { role: 'user', content: text }];
    setHistory(next);
    setLoading(true);
    try {
      const res = await fetch(`${API_ORIGIN}/v1/public/showcase/${encodeURIComponent(slug)}/agent`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        // Send last 6 turns so the model has context. The server
        // re-clamps on its side anyway.
        body: JSON.stringify({
          message: text,
          history: next.slice(-7, -1).map((m) => ({ role: m.role, content: m.content })),
        }),
      });
      if (!res.ok) {
        if (res.status === 429) {
          throw new Error('Muitas perguntas em pouco tempo. Tenta de novo em alguns minutos.');
        }
        throw new Error(`Não consegui responder agora (HTTP ${res.status}).`);
      }
      const json = (await res.json()) as {
        data?: { reply: string; leadIntent?: boolean };
        error?: { message: string };
      };
      const reply = json.data?.reply ?? 'Sem resposta.';
      const leadIntent = json.data?.leadIntent === true;
      setHistory((h) => [...h, { role: 'assistant', content: reply }]);
      if (leadIntent) {
        // Wave 75 · the server flagged the visitor's question as
        // purchase intent. Hand off to the LeadForm: scroll into view +
        // pre-fill the message field with the visitor's question so
        // they don't have to retype it. The LeadForm scene listens for
        // this event and wires it to its setMessage state.
        window.dispatchEvent(
          new CustomEvent('housy:lead-intent', {
            detail: { question: text },
          }),
        );
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro inesperado.');
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  };

  return (
    <>
      {/* Launcher — always visible, fixed bottom-right */}
      <button
        type="button"
        aria-label={open ? 'Fechar chat' : 'Abrir chat'}
        onClick={() => setOpen((o) => !o)}
        // Wave 299 — h-12 = 48 px (already exceeds the WCAG 2.5.5 44 px
        // minimum). pb-safe for iPhone home-indicator clearance when the
        // pill anchors at the bottom of a fixed viewport.
        className="fixed bottom-5 right-5 z-50 flex h-12 min-w-[44px] items-center gap-2 rounded-full border border-white/12 bg-[#0b0a09]/95 px-4 text-[13px] font-medium text-[#e8e5df] shadow-[0_8px_24px_rgba(0,0,0,0.35)] backdrop-blur transition-transform duration-150 hover:bg-[#161513] active:scale-[0.97]"
        style={{
          fontFamily: 'Inter, system-ui, sans-serif',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        <span
          aria-hidden
          className="inline-block h-2 w-2 rounded-full"
          style={{ background: open ? '#888' : '#c87137' }}
        />
        {open ? t('housy.launchClose') : t('housy.launchOpen')}
      </button>

      {/* Panel */}
      {open && (
        <div
          className="fixed bottom-20 right-5 z-50 flex w-[360px] max-w-[calc(100vw-2.5rem)] flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#0d0c0b]/98 shadow-[0_20px_48px_rgba(0,0,0,0.45)] backdrop-blur-xl"
          style={{ fontFamily: 'Inter, system-ui, sans-serif', maxHeight: 'min(72vh, 640px)' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-white/8 px-4 py-3">
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-[#8a857c]">Housy</p>
              <p className="text-[14px] font-medium text-[#e8e5df]">
                Concierge da casa
              </p>
            </div>
            <button
              type="button"
              aria-label="Fechar"
              onClick={() => setOpen(false)}
              className="h-7 w-7 rounded-full text-[#8a857c] hover:bg-white/5 hover:text-[#e8e5df]"
            >
              ×
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 space-y-3 overflow-y-auto px-4 py-4"
            style={{ minHeight: 200 }}
          >
            {history.map((m, i) => (
              <div
                key={i}
                className={`max-w-[88%] rounded-2xl px-3.5 py-2 text-[13.5px] leading-relaxed ${
                  m.role === 'assistant'
                    ? 'bg-[#161513] text-[#e8e5df]'
                    : 'ml-auto bg-[#c87137]/85 text-[#0b0a09]'
                }`}
              >
                {m.content}
              </div>
            ))}
            {loading && (
              <div className="max-w-[88%] rounded-2xl bg-[#161513] px-3.5 py-2 text-[13.5px] text-[#8a857c]">
                pensando…
              </div>
            )}
            {error && (
              <div className="max-w-[88%] rounded-2xl border border-red-700/30 bg-red-900/15 px-3.5 py-2 text-[12.5px] text-red-300">
                {error}
              </div>
            )}
          </div>

          {/* Composer */}
          <div className="border-t border-white/8 p-3">
            <div className="flex items-end gap-2 rounded-xl border border-white/8 bg-[#0b0a09] px-3 py-2 focus-within:border-white/20">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKeyDown}
                rows={1}
                placeholder="Pergunte sobre área, materiais, bairro, status…"
                className="flex-1 resize-none bg-transparent text-[13.5px] leading-relaxed text-[#e8e5df] outline-none placeholder:text-[#5e584d]"
                style={{ minHeight: 22, maxHeight: 120 }}
                maxLength={500}
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => void send()}
                disabled={loading || draft.trim().length < 2}
                className="rounded-lg bg-[#c87137] px-3 py-1.5 text-[12px] font-medium text-[#0b0a09] hover:bg-[#d68348] disabled:opacity-40"
              >
                Enviar
              </button>
            </div>
            <p className="mt-2 text-[10.5px] text-[#5e584d]">
              Powered by Housy · Builder
            </p>
          </div>
        </div>
      )}
    </>
  );
}
